import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Label, ListBox, Select } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { DatePickerField } from '@/components/datePickerField';
import { getNodeTestIdProp } from '@/utils';
import {
    UNIT_LABEL_KEY,
    UNIT_OPTIONS,
    VAT_LABEL_KEY,
    VAT_OPTIONS,
} from '../createProduct/createProductMeta';
import type { useEditProductForm } from './useEditProductForm';
import type { EditProductFormState, EditVariantDraft } from './editProductMeta';

type EditProductForm = ReturnType<typeof useEditProductForm>;

type EditProductViewProps = {
    form: EditProductForm;
    state: EditProductFormState;
};

const NONE_VALUE = '__none__';

const inputClass =
    'w-full rounded-lg border border-border bg-surface-secondary px-3 py-2 text-sm text-foreground placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-primary';

export const EditProductView: React.FC<EditProductViewProps> = ({
    form,
    state,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex flex-col gap-6"
            {...getNodeTestIdProp('edit-product')}
        >
            <div className="flex flex-col gap-3">
                <Button
                    className="self-start"
                    onPress={form.onCancel}
                    size="sm"
                    variant="ghost"
                    {...getNodeTestIdProp('edit-product-back')}
                >
                    <Icon aria-hidden icon="gravity-ui:arrow-left" />
                    {t('products.edit.back')}
                </Button>
                <h1 className="text-2xl font-semibold text-foreground">
                    {t('products.edit.title')}
                </h1>
            </div>

            <Widget
                className="flex flex-col gap-4 p-5"
                {...getNodeTestIdProp('edit-product-identity')}
            >
                <h2 className="text-sm font-semibold text-foreground">
                    {t('products.edit.sections.identity')}
                </h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <LabeledInput
                        label={t('products.edit.fields.name')}
                        onChange={form.onNameChange}
                        slot="name"
                        value={state.name}
                    />
                    <LabeledInput
                        label={t('products.edit.fields.sku')}
                        onChange={form.onSkuChange}
                        slot="sku"
                        value={state.sku}
                    />
                    <LabeledSelect
                        label={t('products.edit.fields.category')}
                        onChange={(value) =>
                            form.onCategoryChange(
                                value === NONE_VALUE ? null : value,
                            )
                        }
                        options={form.categoryOptions}
                        slot="category"
                        value={state.categoryId ?? NONE_VALUE}
                    />
                    <LabeledSelect
                        includeNone={t('products.edit.fields.brandNone')}
                        label={t('products.edit.fields.brand')}
                        onChange={(value) =>
                            form.onBrandChange(
                                value === NONE_VALUE ? null : value,
                            )
                        }
                        options={form.brandOptions}
                        slot="brand"
                        value={state.brandId ?? NONE_VALUE}
                    />
                    <LabeledSelect
                        label={t('products.edit.fields.unit')}
                        onChange={(value) =>
                            form.onUnitChange(
                                value as (typeof UNIT_OPTIONS)[number],
                            )
                        }
                        options={UNIT_OPTIONS.map((option) => ({
                            id: option,
                            name: t(UNIT_LABEL_KEY[option]),
                        }))}
                        slot="unit"
                        value={state.unit}
                    />
                    <LabeledSelect
                        label={t('products.edit.fields.vat')}
                        onChange={(value) =>
                            form.onVatChange(
                                value as (typeof VAT_OPTIONS)[number],
                            )
                        }
                        options={VAT_OPTIONS.map((option) => ({
                            id: option,
                            name: t(VAT_LABEL_KEY[option]),
                        }))}
                        slot="vat"
                        value={state.vatType}
                    />
                </div>
            </Widget>

            <section
                className="flex flex-col gap-3"
                {...getNodeTestIdProp('edit-product-variants')}
            >
                <h2 className="text-sm font-semibold text-foreground">
                    {t('products.edit.sections.variants')} (
                    {state.variants.length})
                </h2>
                <p className="text-xs text-muted">
                    {t('products.edit.variants.hint')}
                </p>
                {state.variants.map((variant) => (
                    <EditVariantCard
                        form={form}
                        key={variant.id}
                        variant={variant}
                    />
                ))}
            </section>

            {form.submitError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('edit-product-error')}
                >
                    {form.submitError.kind === 'i18n'
                        ? t(form.submitError.key)
                        : form.submitError.message}
                </p>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Button
                        isPending={form.isDeleting}
                        onPress={form.onDelete}
                        variant="danger"
                        {...getNodeTestIdProp('edit-product-delete')}
                    >
                        <Icon icon="gravity-ui:trash-bin" />
                        {t('products.edit.delete')}
                    </Button>
                    {form.deleteConflict ? (
                        <Button
                            isPending={form.isArchiving}
                            onPress={form.onArchiveInstead}
                            variant="secondary"
                            {...getNodeTestIdProp('edit-product-archive')}
                        >
                            <Icon icon="gravity-ui:box" />
                            {t('products.edit.archiveInstead')}
                        </Button>
                    ) : null}
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        onPress={form.onCancel}
                        variant="outline"
                        {...getNodeTestIdProp('edit-product-cancel')}
                    >
                        {t('products.edit.cancel')}
                    </Button>
                    <Button
                        isPending={form.isSubmitting}
                        onPress={form.onSubmit}
                        variant="primary"
                        {...getNodeTestIdProp('edit-product-submit')}
                    >
                        {t('products.edit.submit')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

type EditVariantCardProps = {
    form: EditProductForm;
    variant: EditVariantDraft;
};

const EditVariantCard: React.FC<EditVariantCardProps> = ({ form, variant }) => {
    const { t } = useTranslation('pages');

    return (
        <Widget
            className="flex flex-col gap-4 p-4"
            {...getNodeTestIdProp(`edit-product-variant-${variant.id}`)}
        >
            <h3
                className="text-sm font-medium text-foreground"
                {...getNodeTestIdProp(
                    `edit-product-variant-label-${variant.id}`,
                )}
            >
                {variant.label}
            </h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <LabeledInput
                    label={t('products.edit.variants.salePrice')}
                    onChange={(value) =>
                        form.onVariantFieldChange(
                            variant.id,
                            'salePrice',
                            value,
                        )
                    }
                    slot={`variant-${variant.id}-salePrice`}
                    type="number"
                    value={variant.salePrice}
                />
                <LabeledInput
                    label={t('products.edit.variants.minimum')}
                    onChange={(value) =>
                        form.onVariantFieldChange(
                            variant.id,
                            'minimumQuantity',
                            value,
                        )
                    }
                    slot={`variant-${variant.id}-minimum`}
                    type="number"
                    value={variant.minimumQuantity}
                />
                <LabeledInput
                    label={t('products.edit.variants.wholesalePrice')}
                    onChange={(value) =>
                        form.onVariantFieldChange(
                            variant.id,
                            'wholesalePrice',
                            value,
                        )
                    }
                    slot={`variant-${variant.id}-wholesalePrice`}
                    type="number"
                    value={variant.wholesalePrice}
                />
                <LabeledInput
                    label={t('products.edit.variants.weight')}
                    onChange={(value) =>
                        form.onVariantFieldChange(variant.id, 'weight', value)
                    }
                    slot={`variant-${variant.id}-weight`}
                    type="number"
                    value={variant.weight}
                />
                <LabeledInput
                    label={t('products.edit.variants.volume')}
                    onChange={(value) =>
                        form.onVariantFieldChange(variant.id, 'volume', value)
                    }
                    slot={`variant-${variant.id}-volume`}
                    type="number"
                    value={variant.volume}
                />
                <DatePickerField
                    id={`edit-product-input-variant-${variant.id}-saleEndDate`}
                    label={t('products.edit.variants.saleEndDate')}
                    name={`variant-${variant.id}-saleEndDate`}
                    onChange={(value) =>
                        form.onVariantFieldChange(
                            variant.id,
                            'saleEndDate',
                            value,
                        )
                    }
                    value={variant.saleEndDate}
                />
            </div>
        </Widget>
    );
};

/* -------------------------------------------------------------------------- */
/* Render-only field primitives (self-contained to this folder)              */
/* -------------------------------------------------------------------------- */

type LabeledInputProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    slot: string;
    type?: 'text' | 'number';
};

const LabeledInput: React.FC<LabeledInputProps> = ({
    label,
    value,
    onChange,
    slot,
    type = 'text',
}) => (
    <label className="flex flex-col gap-1">
        <span className="text-xs text-muted">{label}</span>
        <input
            className={inputClass}
            onChange={(event) => onChange(event.target.value)}
            type={type}
            value={value}
            {...getNodeTestIdProp(`edit-product-input-${slot}`)}
        />
    </label>
);

type SelectOption = { id: string; name: string; depth?: number };

type LabeledSelectProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    slot: string;
    includeNone?: string;
};

const LabeledSelect: React.FC<LabeledSelectProps> = ({
    label,
    value,
    onChange,
    options,
    slot,
    includeNone,
}) => (
    <Select
        fullWidth
        onChange={(next) => onChange(String(next))}
        value={value}
        variant="secondary"
    >
        <Label className="text-xs text-muted">{label}</Label>
        <Select.Trigger {...getNodeTestIdProp(`edit-product-select-${slot}`)}>
            <Select.Value />
            <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
            <ListBox>
                {includeNone ? (
                    <ListBox.Item id={NONE_VALUE} textValue={includeNone}>
                        {includeNone}
                        <ListBox.ItemIndicator />
                    </ListBox.Item>
                ) : null}
                {options.map((option) => (
                    <ListBox.Item
                        id={option.id}
                        key={option.id}
                        textValue={option.name}
                    >
                        <span
                            style={{
                                paddingInlineStart: option.depth
                                    ? `${option.depth * 12}px`
                                    : undefined,
                            }}
                        >
                            {option.name}
                        </span>
                        <ListBox.ItemIndicator />
                    </ListBox.Item>
                ))}
            </ListBox>
        </Select.Popover>
    </Select>
);
